import { Upload } from "@tamagui/lucide-icons";
import { useToastController } from "@tamagui/toast";
import type { DocumentPickerAsset } from "expo-document-picker";
import type { ImagePickerAsset } from "expo-image-picker";
import { useCallback, useEffect, useState } from "react";
import { XStack, useMedia } from "tamagui";

import type { AskUserQuestionPhotoUpload } from "@/api/generated/models";
import type { AttachmentType } from "@/api/mutations/conversation";
import { Paragraph, Text, YStack } from "@/components";
import AttachmentsButton, {
  ALLOWED_ATTACHMENT_MIME_TYPES,
} from "@/components/AttachmentsButton";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";
import useFileDropZone from "@/hooks/useFileDropZone";
import useHandleAttachmentsPicked from "@/hooks/useHandleAttachmentsPicked";
import { useAskUserQuestionsStore } from "@/store/askUserQuestions";
import PhotoUploadSummary from "./PhotoUploadSummary";

export const MAX_SINGLE_FILE_SIZE = 25 * 1024 * 1024;

export default function PhotoUploadQuestion({
  question,
  questionIndex,
  value,
  onChange,
  onSubmit,
  onSkip,
  isLast,
}: {
  question: AskUserQuestionPhotoUpload;
  questionIndex: number;
  value: string[];
  onChange: (value: string[]) => void;
  onSubmit: () => void;
  onSkip: () => void;
  isLast: boolean;
}) {
  const { setAttachments, session } = useAskUserQuestionsStore();
  const attachments = session!.attachments[questionIndex] ?? [];
  const [attachmentCount, setAttachmentCount] = useState(0);
  const [attachmentSize, setAttachmentSize] = useState(0);
  const toast = useToastController();
  const { lg } = useMedia();

  const { handleAttachmentsPicked, isUploading } = useHandleAttachmentsPicked({
    setAttachments: (value) => setAttachments(questionIndex, value),
    attachmentCount,
    attachmentSize,
    setAttachmentCount,
    setAttachmentSize,
  });

  const hasPhotos = attachments.length > 0;
  const hasPendingAttachments = attachments.some(
    (a) => !a.attachmentId && !a.error,
  );

  const onFilesPicked = useCallback(
    (assets: (DocumentPickerAsset | ImagePickerAsset)[], files: File[]) => {
      const validAssets: (DocumentPickerAsset | ImagePickerAsset)[] = [];
      const validFiles: File[] = [];
      const oversized: AttachmentType[] = [];
      let hasUnsupportedType = false;
      files.forEach((file, i) => {
        if (!ALLOWED_ATTACHMENT_MIME_TYPES.has(file.type.toLowerCase())) {
          hasUnsupportedType = true;
        } else if (file.size > MAX_SINGLE_FILE_SIZE) {
          oversized.push({
            localUri: assets[i].uri,
            name: file.name,
            mimeType: file.type,
            fileSize: file.size,
            isImage: file.type.startsWith("image/"),
            error: "File is too large. Max size is 25MB",
          });
        } else {
          validAssets.push(assets[i]);
          validFiles.push(file);
        }
      });
      if (hasUnsupportedType) {
        toast.show(
          "That file type isn't supported. Please upload an image or PDF.",
          { duration: 10000 },
        );
      }
      if (oversized.length > 0) {
        setAttachments(questionIndex, (prev) => [...prev, ...oversized]);
      }
      if (validFiles.length > 0) {
        handleAttachmentsPicked(validAssets, validFiles);
      }
    },
    [handleAttachmentsPicked, setAttachments, questionIndex, toast],
  );

  const { ref: dropZoneRef, isDragging } = useFileDropZone({
    enabled: lg,
    onFiles: onFilesPicked,
  });

  useEffect(() => {
    const uploaded = attachments
      .map((a) => a.attachmentId)
      .filter((id): id is string => !!id);
    if (uploaded.join(",") !== value.join(",")) onChange(uploaded);
  }, [attachments, value, onChange]);

  const blocking = hasPendingAttachments || isUploading;
  const statusText = blocking
    ? "Uploading…"
    : `${value.length} file${value.length === 1 ? "" : "s"} added`;

  return (
    <>
      {lg ? (
        <YStack
          ref={dropZoneRef}
          alignItems="center"
          justifyContent="center"
          paddingVertical="$400"
          marginHorizontal="$150"
          borderWidth="$strokeWidth"
          borderStyle="dashed"
          borderColor={isDragging ? "$fillButtonPrimary" : "$strokeNeutral"}
          backgroundColor={isDragging ? "$fillCallout" : "$transparent"}
          borderRadius="$150"
        >
          <Upload size="$300" color="$iconBase" />
          <Paragraph marginTop="$250" color="$textSecondary" textAlign="center">
            Drag &amp; drop file(s) to upload
          </Paragraph>
          <AttachmentsButton
            trigger={
              <Text bold textDecorationLine="underline">
                or browse
              </Text>
            }
            triggerProps={{ padding: 0, marginVertical: 0 }}
            onAttachmentsPicked={onFilesPicked}
            isUploading={isUploading}
          />
        </YStack>
      ) : (
        <AttachmentsButton
          trigger={
            <YStack alignItems="center" gap="$150">
              <Upload size="$300" color="$iconBase" />
              <Paragraph color="$textSecondary">Click to upload</Paragraph>
            </YStack>
          }
          triggerProps={{
            marginHorizontal: "$150",
            marginVertical: 0,
            padding: 0,
            paddingVertical: "$400",
            borderWidth: "$strokeWidth",
            borderStyle: "dashed",
            borderColor: "$strokeNeutral",
            borderRadius: "$150",
            justifyContent: "center",
          }}
          onAttachmentsPicked={onFilesPicked}
          isUploading={isUploading}
        />
      )}
      {hasPhotos && (
        <PhotoUploadSummary
          attachments={attachments}
          onRemove={(localUri) => {
            const toRemove = attachments.find((a) => a.localUri === localUri);
            if (toRemove) {
              setAttachmentCount(attachmentCount - 1);
              setAttachmentSize(attachmentSize - (toRemove.fileSize || 0));
            }
            setAttachments(questionIndex, (prev) =>
              prev.filter((a) => a.localUri !== localUri),
            );
          }}
        />
      )}
      <XStack
        alignItems="center"
        gap="$200"
        paddingHorizontal="$150"
        marginTop="$250"
        paddingBottom="$150"
      >
        <Paragraph flex={1} size="$xs" color="$textSecondary">
          {statusText}
        </Paragraph>
        <SkipOrSubmit
          showSubmit
          submitDisabled={value.length === 0 || blocking}
          isLast={isLast}
          onSkip={onSkip}
          onSubmit={onSubmit}
        />
      </XStack>
    </>
  );
}
